import { useCallback, useState, useEffect } from 'react';
import { ErrorOption, useForm, FormProvider } from 'react-hook-form';

import { Organization, UserTypeEnum } from '@Types';
import { useSetFieldErrors } from '@Hooks';
import { useSignUp, SignUpVariables } from '@Hooks/Api';
import { useRouter } from '@Helpers/useRouter';
import { QueryParams, RoutingPaths } from '@App/paths';
import * as authToken from '@Helpers/authToken';

import { SignUpProps } from './SignUp';
import dynamic from 'next/dynamic';
import { useOrganizationLookup } from '@Hooks/useOrganizationLookup';
import { DuplicateOrganizationWarningModal } from './DuplicateOrganizationWarningModal';
import { setBackendFieldErrors } from '@Helpers/setBackendFieldErrors';
import { useNotificationsContext } from '@Contexts';
import { useTranslation } from 'react-i18next';

/** TODO: Workaround for class name hydration issue between server and client */
const SignUpNoSSR = dynamic<SignUpProps>(
  () => import('./SignUp').then((mod) => mod.SignUp) as any,
  {
    ssr: false,
  },
);

interface SignUpControllerProps {
  userType: UserTypeEnum | undefined;
  nominationToken?: string;
  defaultOrganizationName?: string;
  defaultEmail?: string;
  applyFundId?: number;
}

export const SignUpController = ({
  userType,
  nominationToken,
  defaultOrganizationName,
  defaultEmail,
  applyFundId,
}: SignUpControllerProps) => {
  const { push, params } = useRouter();
  const methods = useForm({
    defaultValues: {
      organisation_name: defaultOrganizationName,
      email: defaultEmail,
    },
  });
  const { t } = useTranslation('common');
  const { register, handleSubmit, errors, setError: setErrorWronglyTyped, control } = methods;
  // react-hook-form has a typing bug where setting defaultValues in useForm incorrectly changes the type of setError,
  // restricting the allowed error fields to just those with default values.
  // As such, we need to cast it back to the correct type.
  const setError = setErrorWronglyTyped as (name: string, error: ErrorOption) => void;
  const { signUp, response } = useSignUp();
  const { error, loading } = response;
  const { organizationLookup } = useOrganizationLookup();
  const [duplicateOrganizations, setDuplicateOrganizations] = useState<Organization[]>([]);
  const [submittedData, setSubmittedData] = useState<SignUpVariables | null>(null);
  const [buttonsEnabled, setButtonsEnabled] = useState(true);
  const { error: errorNotification } = useNotificationsContext();

  const rawSubmit = useCallback(
    (data: SignUpVariables) => {
      signUp({
        ...data,
        nomination: nominationToken,
        apply_fund_id: applyFundId,
      })
        .catch((err) => {
          if (err?.data?.field_errors) {
            setBackendFieldErrors(err.data.field_errors, setError);
          } else {
            errorNotification(t('common:errors.generic_error'));
          }

          setButtonsEnabled(true);
        })
        .then(() => {
          setButtonsEnabled(true);
        });
    },
    [applyFundId, signUp, nominationToken, errorNotification, setError, t],
  );

  const submitFromModal = useCallback(() => {
    setDuplicateOrganizations([]);
    if (submittedData) {
      rawSubmit(submittedData);
    }
  }, [rawSubmit, setDuplicateOrganizations, submittedData]);

  const onSubmit = useCallback(
    (data: SignUpVariables) => {
      setButtonsEnabled(false);
      const organizationName = data.organisation_name;
      organizationLookup(organizationName).then((result) => {
        const duplicates = result.data;
        if (duplicates.length > 0) {
          // We need to warn the user about possible duplicate organisations before submitting.
          // Store the data we submitted so it can be submitted after the warning modal is accepted.
          setSubmittedData(data);
          setDuplicateOrganizations(duplicates);
        } else {
          rawSubmit(data);
        }
      });
    },
    [setSubmittedData, setDuplicateOrganizations, organizationLookup, rawSubmit],
  );

  const closeDuplicatesModal = useCallback(() => {
    setButtonsEnabled(true);
    setDuplicateOrganizations([]);
  }, [setDuplicateOrganizations]);

  useSetFieldErrors({ fieldErrors: error?.field_errors, setError, loading });

  if (authToken.hasToken()) {
    push(RoutingPaths.DASHBOARD);
    return null;
  }

  return (
    <FormProvider {...methods}>
      <SignUpNoSSR
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        fieldErrors={errors}
        userType={userType}
        isLoading={loading}
        buttonsEnabled={buttonsEnabled}
      />
      <DuplicateOrganizationWarningModal
        organizations={duplicateOrganizations}
        submit={submitFromModal}
        close={closeDuplicatesModal}
      />
    </FormProvider>
  );
};
